import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

interface EmployeeProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * EmployeeProtectedRoute component for employee-only routes
 * Allows employees and promoted employees (those who are also members)
 * Redirects staff without employee role to /dashboard
 * Redirects non-authenticated users to /auth
 */
export default function EmployeeProtectedRoute({ children }: EmployeeProtectedRouteProps) {
  const { user, loading, emailVerified } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if email is verified
  if (!emailVerified) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user has an organization
  if (!user.organizationId) {
    // User exists but has no organization - redirect to no-organization page
    return <Navigate to="/no-organization" replace />;
  }

  // Check if user is an employee (has employee role OR has an employeeId)
  const isEmployee = user.role === 'employee' || user.employeeId;

  if (!isEmployee) {
    // Non-employee users should be redirected to the main dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
